(() => {
  const CARD_SELECTOR = '.collection-grid-section .card-wrapper';
  const MEDIA_SELECTOR = '.card--image-animate.media';
  const DESKTOP_QUERY = '(min-width: 990px)';
  const MOBILE_GALLERY_SNAP_DELAY = 220;
  const MOBILE_GALLERY_SNAP_TIMEOUT = 900;
  const MOBILE_GALLERY_SNAP_TOLERANCE = 1.5;
  const ACTIVE_TALL_SLIDE_CLASS = 'card-wrapper--gallery-active-tall';
  // Demotes a statically-tall card (portrait FIRST image) while the active
  // gallery slide is square, so packshot slides collapse to the square
  // presentation instead of stretching over the whole tile.
  const ACTIVE_SQUARE_SLIDE_CLASS = 'card-wrapper--gallery-active-square';
  const ACTIVE_LIGHT_CAPTION_CLASS = 'caption-gallery-active-light';
  const ACTIVE_DARK_CAPTION_CLASS = 'caption-gallery-active-dark';
  const DESKTOP_ACTIVE_SLIDE_CLASS = 'card__media-slide--desktop-active';
  const DESKTOP_EXITING_CLASS = 'card-wrapper--gallery-exiting';
  const SWATCH_HOVER_CLASS = 'card-wrapper--swatch-hover';

  // Extra gallery slides are rendered without src or srcset (card-product.liquid).
  // An image that already has a src was re-pointed by a swatch tap and must keep it.
  const hydrateDeferredSlides = (target) => {
    if (!(target instanceof Element)) return;
    const card = target.closest('.card-wrapper');
    const media = card
      ? card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`)
      : target.closest('.card__media-gallery');
    if (!media) return;

    const widths = media.dataset.deferredWidths || '';
    media.querySelectorAll(':scope > img[data-deferred-src]').forEach((image) => {
      if (!image.hasAttribute('src')) {
        if (widths) image.srcset = cardMedia.imageTagSrcset(image.dataset.deferredSrc, widths);
        image.src = image.dataset.deferredSrc;
      }
      image.removeAttribute('data-deferred-src');
    });
  };

  ['pointerdown', 'mouseover', 'focusin'].forEach((type) => {
    document.addEventListener(type, (event) => hydrateDeferredSlides(event.target), { capture: true, passive: true });
  });
  document.addEventListener('scroll', (event) => {
    if (event.target instanceof Element && event.target.matches('.card__media-gallery')) hydrateDeferredSlides(event.target);
  }, { capture: true, passive: true });

  const prefersParentCarouselDrag = (card) =>
    Boolean(card.closest('.featured-collection-w-image slider-component[data-draggable]'));

  const getActiveSwatch = (card) =>
    card.querySelector('.color-swatch.is-active') ||
    card.querySelector('.color-swatch.swatch-selected') ||
    card.querySelector('.color-swatch');

  const getGalleryItems = (swatch) => cardMedia.swatchGalleryItems(swatch);

  const getSecondaryImage = (card, media) => {
    let image = media.querySelector(':scope > img.card__media-secondary');
    const template = media.querySelector(':scope > img:first-child');
    if (!image && template) {
      image = template.cloneNode(false);
      image.className = 'motion-reduce card__media-secondary';
      media.appendChild(image);
    }
    return image;
  };

  const setHoverRatioClass = (card, item, image) => {
    let ratio = item.ratio;
    if (!ratio && image.naturalWidth > 0 && image.naturalHeight > 0) {
      ratio = image.naturalWidth / image.naturalHeight;
    }

    if (ratio) {
      card.classList.toggle('card-wrapper--hover-tall', ratio < 0.95 || ratio > 1.05);
    }
  };

  const isNonSquareRatio = (ratio) => ratio > 0 && (ratio < 0.95 || ratio > 1.05);

  const getImageRatio = (image, fallback = 0) => {
    if (!image) return fallback;

    const width = image.naturalWidth || parseFloat(image.getAttribute('width') || '0');
    const height = image.naturalHeight || parseFloat(image.getAttribute('height') || '0');

    return width > 0 && height > 0 ? width / height : fallback;
  };

  const getCardGalleryItems = (card, media = null) => {
    const swatchItems = getGalleryItems(getActiveSwatch(card));
    if (swatchItems.length > 0) return swatchItems;

    const gallery = media || card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
    if (!gallery) return [];

    return Array.from(gallery.querySelectorAll(':scope > img')).map((image) => ({
      src: image.currentSrc || image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      alt: image.getAttribute('alt') || '',
      ratio: getImageRatio(image)
    }));
  };

  const preloadImage = (image, highPriority = false) => {
    if (!image) return Promise.resolve();

    const src = image.currentSrc || image.getAttribute('src') || '';
    const srcset = image.getAttribute('srcset') || '';
    if (!src && !srcset) return Promise.resolve();

    image.loading = 'eager';
    image.fetchPriority = highPriority ? 'high' : 'low';

    return new Promise((resolve) => {
      if (image.complete && image.naturalWidth > 0) {
        if (image.decode) image.decode().catch(() => {}).finally(resolve);
        else resolve();
        return;
      }

      const preload = new Image();
      const finish = () => {
        if (preload.decode) preload.decode().catch(() => {}).finally(resolve);
        else resolve();
      };

      preload.onload = finish;
      preload.onerror = resolve;
      preload.fetchPriority = highPriority ? 'high' : 'low';
      preload.sizes = image.getAttribute('sizes') || image.sizes || '';
      if (srcset) preload.srcset = srcset;
      preload.src = src;
    });
  };

  const warmGalleryImages = (card) => {
    const media = card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
    if (!media) return Promise.resolve();

    // A first swipe only needs the current and adjacent slides. Warming every
    // gallery image for each approaching PLP card made mobile scrolling fight
    // image download/decoding work, especially on collections with variants.
    const images = Array.from(media.querySelectorAll(':scope > img')).slice(0, 2);
    return Promise.all(images.map((image) => preloadImage(image, false)));
  };

  const normalizeIndex = (index, length) => ((index % length) + length) % length;

  const clearDesktopGalleryState = (card) => {
    const media = card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
    if (media) {
      media.querySelectorAll(':scope > img').forEach((image) => {
        image.classList.remove(DESKTOP_ACTIVE_SLIDE_CLASS);
        image.removeAttribute('aria-hidden');
      });
    }

    card.classList.remove('card-wrapper--gallery-stepped', DESKTOP_EXITING_CLASS);
    delete card.dataset.galleryIndex;

    const swatch = getActiveSwatch(card);
    const hoverImage = media?.querySelector(':scope > img.card__media-secondary') || media?.querySelector(':scope > img:nth-child(2)');
    const hoverRatio = parseFloat(swatch?.dataset.hoverAspectRatio || '0') || getImageRatio(hoverImage);
    card.classList.toggle('card-wrapper--hover-tall', isNonSquareRatio(hoverRatio));
  };

  const setDesktopGalleryIndex = (card, index, stepped = false) => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) return false;

    const media = card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
    const images = media ? Array.from(media.querySelectorAll(':scope > img')) : [];
    const items = getCardGalleryItems(card, media);
    const length = Math.min(items.length, images.length);
    if (!media || length < 2) {
      clearDesktopGalleryState(card);
      return false;
    }

    const nextIndex = normalizeIndex(index, length);
    images.forEach((image, imageIndex) => {
      image.classList.toggle(DESKTOP_ACTIVE_SLIDE_CLASS, imageIndex === nextIndex);
      image.setAttribute('aria-hidden', imageIndex === nextIndex ? 'false' : 'true');
    });

    card.dataset.galleryIndex = String(nextIndex);
    card.classList.toggle('card-wrapper--gallery-stepped', stepped);
    setHoverRatioClass(card, items[nextIndex], images[nextIndex]);
    preloadImage(images[nextIndex], true);
    return true;
  };

  const getActiveGalleryIndex = (media) => {
    const images = media.querySelectorAll(':scope > img');
    const slideWidth = media.clientWidth || 1;
    const index = Math.round(media.scrollLeft / slideWidth);

    return Math.max(0, Math.min(images.length - 1, index));
  };

  const applyMobileGallerySlideState = (card, forcedIndex = null) => {
    const media = card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
    // An off-screen gallery skipped by content-visibility (component-card.css)
    // reports scrollLeft 0 until it renders again, which would set the
    // classes of the first slide; keep those of the slide it comes back on.
    if (forcedIndex === null && media?.firstElementChild?.checkVisibility?.({ contentVisibilityAuto: true }) === false) return;
    card.classList.remove(ACTIVE_LIGHT_CAPTION_CLASS, ACTIVE_DARK_CAPTION_CLASS);

    if (window.matchMedia(DESKTOP_QUERY).matches || !media) {
      card.classList.remove(ACTIVE_TALL_SLIDE_CLASS, ACTIVE_SQUARE_SLIDE_CLASS);
      return;
    }

    const images = media.querySelectorAll(':scope > img');
    const measuredIndex = forcedIndex === null ? getActiveGalleryIndex(media) : forcedIndex;
    const index = Math.max(0, Math.min(images.length - 1, measuredIndex));
    const image = images[index];
    const item = getCardGalleryItems(card, media)[index];
    const ratio = getImageRatio(image, item ? item.ratio : 0);
    const activeTall = isNonSquareRatio(ratio);

    card.classList.toggle(ACTIVE_TALL_SLIDE_CLASS, activeTall);
    card.classList.toggle(ACTIVE_SQUARE_SLIDE_CLASS, ratio > 0 && !activeTall);

    if (!activeTall) {
      card.classList.add(ACTIVE_DARK_CAPTION_CLASS);
      return;
    }

    if (index === 0 && card.classList.contains('caption-contrast-light')) {
      card.classList.add(ACTIVE_LIGHT_CAPTION_CLASS);
    } else if (index === 1 && card.classList.contains('caption-hover-light')) {
      card.classList.add(ACTIVE_LIGHT_CAPTION_CLASS);
    } else if (index === 1 && card.classList.contains('caption-hover-dark')) {
      card.classList.add(ACTIVE_DARK_CAPTION_CLASS);
    }
  };

  const syncMobileActiveGallerySlide = (card) => applyMobileGallerySlideState(card);

  const showGalleryItem = (card, index) => {
    const media = card.querySelector(MEDIA_SELECTOR);
    const isGallery = media?.classList.contains('card__media-gallery');
    const items = isGallery ? getCardGalleryItems(card, media) : getGalleryItems(getActiveSwatch(card));
    if (!media || items.length < 2) return;

    if (isGallery && setDesktopGalleryIndex(card, index, true)) {
      return;
    }

    const image = getSecondaryImage(card, media);
    if (!image) return;

    const nextIndex = normalizeIndex(index, items.length);
    const item = items[nextIndex];

    if (item.srcset) {
      image.srcset = item.srcset;
    } else {
      image.removeAttribute('srcset');
    }
    image.src = item.src;
    image.alt = item.alt;

    card.dataset.galleryIndex = String(nextIndex);
    card.classList.add('card-wrapper--gallery-stepped');
    setHoverRatioClass(card, item, image);
    card.dispatchEvent(new CustomEvent('card:media-changed', {
      bubbles: true,
      detail: { galleryControls: true }
    }));
  };

  const resetGalleryIndex = (card) => {
    const media = card.querySelector(MEDIA_SELECTOR);
    const items = media?.classList.contains('card__media-gallery')
      ? getCardGalleryItems(card, media)
      : getGalleryItems(getActiveSwatch(card));
    if (items.length < 2) {
      card.classList.remove('card-wrapper--gallery-stepped');
      delete card.dataset.galleryIndex;
      return;
    }

    showGalleryItem(card, 1);
    card.classList.remove('card-wrapper--gallery-stepped');
  };

  const handleStep = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const card = button.closest(CARD_SELECTOR);
    const currentIndex = Number(card.dataset.galleryIndex || 1);
    const direction = button.dataset.galleryDirection === 'previous' ? -1 : 1;
    showGalleryItem(card, currentIndex + direction);
  };

  const stepCard = (card, direction) => {
    const currentIndex = Number(card.dataset.galleryIndex || 1);
    showGalleryItem(card, currentIndex + direction);
  };

  const syncControls = (card) => {
    const hasGallery = getCardGalleryItems(card).length > 1;
    card.classList.toggle('card-wrapper--has-gallery-controls', hasGallery);

    const controls = card.querySelector('.card-gallery-controls');
    if (controls) controls.hidden = !hasGallery;
  };

  const setupCard = (card) => {
    if (card.dataset.galleryControlsReady === 'true') return;
    card.dataset.galleryControlsReady = 'true';

    const mediaWrapper = card.querySelector('.card__media.media-wrapper');
    if (!mediaWrapper) return;

    const controls = document.createElement('div');
    controls.className = 'card-gallery-controls';
    controls.hidden = true;
    controls.innerHTML = `
      <button class="card-gallery-controls__button card-gallery-controls__button--prev" type="button" data-gallery-direction="previous" aria-label="Image précédente">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4 7 12l8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button class="card-gallery-controls__button card-gallery-controls__button--next" type="button" data-gallery-direction="next" aria-label="Image suivante">
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 4 8 8-8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    `;

    card.appendChild(controls);
    controls.querySelectorAll('button').forEach((button) => button.addEventListener('click', handleStep));

    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerStartScrollLeft = 0;
    let pointerActive = false;
    let pointerSwiped = false;
    let pointerMedia = null;
    let nativeSwipeActive = false;
    let nativeSwipeMedia = null;
    let suppressNativeLayoutSyncUntil = 0;
    let scrollRaf = 0;
    let mobileSnapTimer = 0;
    let mobileSnapRaf = 0;
    let mobileSnapStartedAt = 0;
    let mobileLastScrollLeft = 0;
    let mobileStableFrames = 0;
    let desktopExitTimer = 0;

    const getMobileGalleryMedia = () => mediaWrapper.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);

    const cancelSettledMobileSnap = () => {
      clearTimeout(mobileSnapTimer);
      cancelAnimationFrame(mobileSnapRaf);
    };

    const finishSettledMobileSnap = (media, targetLeft) => {
      const offset = Math.abs(media.scrollLeft - targetLeft);

      if (offset > MOBILE_GALLERY_SNAP_TOLERANCE) {
        media.scrollTo({ left: targetLeft, behavior: 'auto' });
      }
    };

    const waitForMobileGallerySettleToSnap = (media) => {
      if (!media || !media.isConnected || window.matchMedia(DESKTOP_QUERY).matches) return;

      if (nativeSwipeActive || isNativeLayoutSyncSuppressed()) {
        mobileSnapTimer = setTimeout(() => waitForMobileGallerySettleToSnap(media), 80);
        return;
      }

      const images = media.querySelectorAll(':scope > img');
      const slideWidth = media.clientWidth || 1;
      const slideIndex = Math.max(0, Math.min(images.length - 1, Math.round(media.scrollLeft / slideWidth)));
      const targetLeft = slideIndex * slideWidth;
      const currentLeft = media.scrollLeft;
      const moved = Math.abs(currentLeft - mobileLastScrollLeft) > 0.35;
      const timedOut = Date.now() - mobileSnapStartedAt > MOBILE_GALLERY_SNAP_TIMEOUT;

      mobileStableFrames = moved ? 0 : mobileStableFrames + 1;
      mobileLastScrollLeft = currentLeft;

      if (mobileStableFrames >= 2 || timedOut) {
        finishSettledMobileSnap(media, targetLeft);
        return;
      }

      mobileSnapRaf = requestAnimationFrame(() => waitForMobileGallerySettleToSnap(media));
    };

    const scheduleSettledMobileSnap = (delay = MOBILE_GALLERY_SNAP_DELAY) => {
      if (window.matchMedia(DESKTOP_QUERY).matches) return;

      const media = getMobileGalleryMedia();
      if (!media) return;

      cancelSettledMobileSnap();
      mobileSnapStartedAt = Date.now();
      mobileLastScrollLeft = media.scrollLeft;
      mobileStableFrames = 0;
      mobileSnapTimer = setTimeout(() => waitForMobileGallerySettleToSnap(media), delay);
    };

    // Until the gallery has scrolled, the active slide is the first one; skip
    // the scroll measurement, which forces a style recalc after each image load.
    let galleryScrolled = false;
    const requestActiveSlideSync = () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => applyMobileGallerySlideState(card, galleryScrolled ? null : 0));
    };

    const isNativeLayoutSyncSuppressed = () => Date.now() < suppressNativeLayoutSyncUntil;

    const startNativeSwipe = (clientX, clientY) => {
      if (window.matchMedia(DESKTOP_QUERY).matches) return false;
      if (prefersParentCarouselDrag(card)) return false;
      if (getCardGalleryItems(card).length < 2) return false;

      nativeSwipeMedia = mediaWrapper.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
      if (!nativeSwipeMedia) return false;

      cancelSettledMobileSnap();
      warmGalleryImages(card);
      nativeSwipeActive = true;
      return true;
    };

    const finishNativeSwipe = () => {
      if (!nativeSwipeActive) return;
      suppressNativeLayoutSyncUntil = Date.now() + 120;
      nativeSwipeActive = false;
      nativeSwipeMedia = null;
      setTimeout(requestActiveSlideSync, 140);
      setTimeout(requestActiveSlideSync, 320);
      scheduleSettledMobileSnap(180);
    };

    const galleryMedia = mediaWrapper.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
    if (galleryMedia) {
      if ('IntersectionObserver' in window) {
        const warmObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            warmObserver.disconnect();
            warmGalleryImages(card);
          });
        }, { rootMargin: '1200px' });
        warmObserver.observe(card);
      } else {
        warmGalleryImages(card);
      }

      // sync only once the swipe settles: toggling the tall/square classes
      // resizes the scroller, and any re-layout of a scroll-snap container
      // mid-gesture makes the browser re-resolve its snap target — the
      // scroll teleports and slides rest half-snapped
      let scrollSettleTimer = 0;
      galleryMedia.addEventListener('scroll', () => {
        galleryScrolled = true;
        if (nativeSwipeActive || isNativeLayoutSyncSuppressed()) return;
        clearTimeout(scrollSettleTimer);
        scrollSettleTimer = setTimeout(() => {
          syncMobileActiveGallerySlide(card);
          scheduleSettledMobileSnap(0);
        }, 170);
      }, { passive: true });
    }

    mediaWrapper.addEventListener('load', (event) => {
      if (isNativeLayoutSyncSuppressed()) return;
      if (event.target instanceof HTMLImageElement) requestActiveSlideSync();
    }, { capture: true, passive: true });

    mediaWrapper.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      if (prefersParentCarouselDrag(card)) return;
      if (getCardGalleryItems(card).length < 2) return;

      pointerMedia = mediaWrapper.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
      warmGalleryImages(card);
      if (startNativeSwipe(event.clientX, event.clientY)) {
        pointerMedia = null;
        return;
      }

      pointerActive = true;
      pointerSwiped = false;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      pointerStartScrollLeft = pointerMedia ? pointerMedia.scrollLeft : 0;
    }, { passive: true });

    mediaWrapper.addEventListener('pointermove', (event) => {
      if (nativeSwipeActive) return;

      if (!pointerActive) return;

      const deltaX = event.clientX - pointerStartX;
      const deltaY = event.clientY - pointerStartY;
      if (!pointerSwiped) {
        if (Math.abs(deltaX) < 16) return;

        if (Math.abs(deltaX) < Math.abs(deltaY) * 1.25) {
          pointerActive = false;
          pointerMedia = null;
          return;
        }

        pointerSwiped = true;
      }

      if (pointerMedia) {
        event.preventDefault();
        pointerMedia.scrollLeft = pointerStartScrollLeft - deltaX;
        return;
      }

      pointerActive = false;
      stepCard(card, deltaX < 0 ? 1 : -1);
    }, { passive: false });

    mediaWrapper.addEventListener('pointerup', () => {
      finishNativeSwipe();

      if (pointerSwiped && pointerMedia) {
        const slideWidth = pointerMedia.clientWidth || 1;
        const slideIndex = Math.round(pointerMedia.scrollLeft / slideWidth);
        applyMobileGallerySlideState(card, slideIndex);
        pointerMedia.scrollTo({ left: slideIndex * slideWidth, behavior: 'auto' });
        setTimeout(requestActiveSlideSync, 60);
        scheduleSettledMobileSnap(80);
      }

      pointerActive = false;
      pointerMedia = null;
    }, { passive: true });

    mediaWrapper.addEventListener('pointercancel', () => {
      finishNativeSwipe();
      pointerActive = false;
      pointerMedia = null;
    }, { passive: true });

    mediaWrapper.addEventListener('touchstart', (event) => {
      const touch = event.touches && event.touches[0];
      if (!touch || nativeSwipeActive) return;
      startNativeSwipe(touch.clientX, touch.clientY);
    }, { passive: true });

    mediaWrapper.addEventListener('touchend', finishNativeSwipe, { passive: true });
    mediaWrapper.addEventListener('touchcancel', finishNativeSwipe, { passive: true });

    mediaWrapper.addEventListener('click', (event) => {
      if (!pointerSwiped) return;

      event.preventDefault();
      event.stopPropagation();
      pointerSwiped = false;
    }, true);

    // Desktop trackpad: a horizontal two-finger swipe over the media steps
    // the gallery (touch swipes arrive as pointer events, wheel ones don't).
    let wheelAccum = 0;
    let wheelIdleTimer = 0;
    let wheelStepped = false;
    mediaWrapper.addEventListener('wheel', (event) => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      if (prefersParentCarouselDrag(card)) return;
      if (card.closest('.featured-collection-scroll .slider-scroll')) return;
      if (getCardGalleryItems(card).length < 2) return;

      event.preventDefault();
      clearTimeout(wheelIdleTimer);
      wheelIdleTimer = setTimeout(() => {
        wheelAccum = 0;
        wheelStepped = false;
      }, 250);

      if (wheelStepped) return;
      wheelAccum += event.deltaX;
      if (Math.abs(wheelAccum) < 45) return;

      wheelStepped = true;
      stepCard(card, wheelAccum > 0 ? 1 : -1);
      wheelAccum = 0;
    }, { passive: false });

    card.addEventListener('mouseenter', () => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;
      clearTimeout(desktopExitTimer);
      card.classList.remove(DESKTOP_EXITING_CLASS);
      if (!setDesktopGalleryIndex(card, 1)) resetGalleryIndex(card);
      syncControls(card);
    });

    card.addEventListener('mouseleave', () => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) {
        clearDesktopGalleryState(card);
        return;
      }

      clearTimeout(desktopExitTimer);
      const media = card.querySelector(`${MEDIA_SELECTOR}.card__media-gallery`);
      const activeImage = media?.querySelector(`:scope > img.${DESKTOP_ACTIVE_SLIDE_CLASS}`);
      const isFirstSlide = activeImage && activeImage === media.querySelector(':scope > img:first-child');

      if (!activeImage || isFirstSlide) {
        clearDesktopGalleryState(card);
        return;
      }

      card.classList.add(DESKTOP_EXITING_CLASS);
      desktopExitTimer = setTimeout(() => clearDesktopGalleryState(card), 380);
    });

    card.addEventListener('card:media-changed', (event) => {
      if (event.detail && event.detail.galleryControls) return;

      card.classList.remove('card-wrapper--gallery-stepped');
      delete card.dataset.galleryIndex;
      requestAnimationFrame(() => {
        syncControls(card);
        warmGalleryImages(card);
        if (window.matchMedia(DESKTOP_QUERY).matches && card.matches(':hover')) {
          setDesktopGalleryIndex(card, card.classList.contains(SWATCH_HOVER_CLASS) ? 0 : 1);
        }
        syncMobileActiveGallerySlide(card);
      });
    });

    syncControls(card);
    // A card being set up has not been swiped yet. Measuring its scroll position
    // here would force a full-page style recalc per card, since cards are set up
    // in batches right after the previous card's class writes.
    applyMobileGallerySlideState(card, 0);
  };

  const cardSetupObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        setupCard(entry.target);
      });
    }, { rootMargin: '800px 0px' })
    : null;

  const setupCardWhenNearViewport = (card) => {
    if (card.dataset.galleryControlsReady === 'true') return;
    if (cardSetupObserver) {
      cardSetupObserver.observe(card);
    } else {
      setupCard(card);
    }
  };

  const setupAll = (scope = document) => {
    scope.querySelectorAll(CARD_SELECTOR).forEach(setupCardWhenNearViewport);
  };

  // component-card.css snaps the galleries only from here on: WebKit sent
  // galleries that snapped while the page was being parsed to a random slide.
  const enableSnapping = () => document.documentElement.classList.add('card-gallery-snap');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      enableSnapping();
      setupAll();
    });
  } else {
    enableSnapping();
    setupAll();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches(CARD_SELECTOR)) setupCardWhenNearViewport(node);
        setupAll(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  let resizeRaf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
      document.querySelectorAll(`${CARD_SELECTOR}[data-gallery-controls-ready="true"]`).forEach(syncMobileActiveGallerySlide);
    });
  }, { passive: true });
})();
